import { useState, ReactElement } from 'react';
import DataGrid, {
    Paging,
    Column,
    Pager,
    Summary,
    TotalItem,
    MasterDetail,
    Selection,
    KeyboardNavigation,
} from 'devextreme-react/data-grid';
import CustomStore from 'devextreme/data/custom_store';
import 'devextreme/data/odata/store';
import { v4 as uuidv4 } from 'uuid';
import { GridSettingType } from '@apply/constants';
import dxDataGrid, {
    CellClickEvent,
    CellDblClickEvent,
    CellPreparedEvent,
    Column as DevExtremeColumn,
    InitNewRowEvent,
    RowDblClickEvent,
    RowExpandingEvent,
    RowPreparedEvent,
    RowRemovedEvent,
} from 'devextreme/ui/data_grid';
import { EventInfo } from 'devextreme/events';
import '../../../scss/controls/_standardGrid.scss';

export { Column, Summary, TotalItem, CustomStore, MasterDetail };
export enum StandardGridPresentationType {
    Flat = 'flat',
    Enhanced = 'enhanced',
    EnhancedDeemphasized = 'enhancedDeemphasized',
}

interface StandardGridProps {
    children?: unknown;
    gridType?: GridSettingType;
    dataStore?: CustomStore;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dataSource?: any;
    allowedPageSizes?: number[];
    defaultPageSize?: number;
    onCellPrepared?: (e: CellPreparedEvent<unknown, unknown>) => void;
    onSelectionChanged?: (e: EventInfo<dxDataGrid<unknown, unknown>>) => void;
    onCellClick?: (e: CellClickEvent<unknown, unknown>) => void;
    onCellDblClick?: (e: CellDblClickEvent<unknown, unknown>) => void;
    onRowDblClick?: (e: RowDblClickEvent<unknown, unknown>) => void;
    customizeColumns?: (columns: DevExtremeColumn<unknown, unknown>[]) => void;
    onContentReady?: (e: EventInfo<dxDataGrid<unknown, unknown>>) => void;
    allowColumnReordering?: boolean;
    onRowPrepared?: (e: RowPreparedEvent<unknown, unknown>) => void;
    onRowRemoved?: (e: RowRemovedEvent<unknown, unknown>) => void;
    onRowExpanding?: (e: RowExpandingEvent<unknown, unknown>) => void;
    onInitNewRow?: (e: InitNewRowEvent<unknown, unknown>) => void;
    wordWrapEnabled?: boolean;
    className?: string;
    width?: number;
    keyExpr?: string;
    allowColumnResizing?: boolean;
    pagerVisible?: boolean;
    showColumnHeaders?: boolean;
    leadPanelEnabled?: boolean;
    loadPanelEnabled?: boolean;
    showBorders?: boolean;
    showRowLines?: boolean;
    rowAlternationEnabled?: boolean;
    selectionMode?: string;
    remoteOperations?: boolean;
    noDataText?: string;
    height?: any;
    presentationType?: StandardGridPresentationType;
}

export const StandardGrid = ({
    children,
    dataStore,
    dataSource,
    onCellPrepared,
    onSelectionChanged,
    onCellClick,
    onCellDblClick,
    onRowDblClick,
    customizeColumns,
    onContentReady,
    onRowPrepared,
    onRowRemoved,
    onRowExpanding,
    onInitNewRow,
    className = 'standardStyle',
    width,
    keyExpr = 'Id',
    allowColumnReordering = true,
    allowedPageSizes = [15, 25, 50],
    defaultPageSize = 25,
    allowColumnResizing = true,
    wordWrapEnabled = false,
    pagerVisible = true,
    showColumnHeaders = true,
    loadPanelEnabled = true,
    showBorders = true,
    showRowLines = true,
    rowAlternationEnabled = true,
    selectionMode = 'none',
    remoteOperations = true,
    noDataText = '',
    height,
    presentationType = StandardGridPresentationType.Flat,
}: StandardGridProps): ReactElement => {
    //Used for Table refresh purposes (useRef doesn't work reliable with the DataGrid)
    const [tableId] = useState(uuidv4());

    const handleContentReady = (e: EventInfo<dxDataGrid<unknown, unknown>>) => {
        if (onContentReady) {
            onContentReady(e);
        }

        //Fixes Devextreme's stupid handling of scroll focus events (prevents selection on scroll, double click still works for selection on text):
        const gridInstance = e.component;
        const rootElement = gridInstance.element() as HTMLElement;
        if (!rootElement) return;

        // Start in "no text select" mode
        rootElement.classList.add('no-text-select');

        let isMouseDown = false;
        let isIntentionalSelect = false;
        let lastX = 0;
        let lastY = 0;

        const onMouseDown = (ev: MouseEvent) => {
            isMouseDown = true;
            isIntentionalSelect = false;
            lastX = ev.clientX;
            lastY = ev.clientY;
        };

        const onMouseMove = (ev: MouseEvent) => {
            if (!isMouseDown) return;

            const deltaX = Math.abs(ev.clientX - lastX);
            const deltaY = Math.abs(ev.clientY - lastY);

            // If user drags inside a cell → intentional selection
            if (deltaX < 3 && deltaY > 3) {
                isIntentionalSelect = true;
                rootElement.classList.remove('no-text-select');
                rootElement.classList.add('allow-text-select');
            }
        };

        const onDblClick = () => {
            // Double-click always means intentional selection
            isIntentionalSelect = true;
            rootElement.classList.remove('no-text-select');
            rootElement.classList.add('allow-text-select');
        };

        const onMouseUp = () => {
            isMouseDown = false;

            // If user was intentionally selecting, keep selection enabled
            if (isIntentionalSelect) return;

            // Otherwise revert to no-selection mode
            rootElement.classList.remove('allow-text-select');
            rootElement.classList.add('no-text-select');
        };

        rootElement.addEventListener('mousedown', onMouseDown);
        rootElement.addEventListener('dblclick', onDblClick);
        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseup', onMouseUp);
    };

    return (
        <DataGrid
            allowColumnReordering={allowColumnReordering}
            allowColumnResizing={allowColumnResizing}
            cellHintEnabled={true}
            className={`standardGrid ${className ?? ''} ${presentationType ?? ''}`}
            columnAutoWidth={false}
            columnHidingEnabled={false}
            columnMinWidth={20}
            columnResizingMode={'nextColumn'}
            customizeColumns={customizeColumns}
            dataSource={dataSource ?? dataStore}
            id={tableId}
            keyExpr={dataSource ? keyExpr : ''}
            loadPanel={{ enabled: loadPanelEnabled }}
            onCellClick={onCellClick}
            onCellDblClick={onCellDblClick}
            onRowDblClick={onRowDblClick}
            onCellPrepared={onCellPrepared}
            onContentReady={handleContentReady}
            onRowPrepared={onRowPrepared}
            onRowRemoved={onRowRemoved}
            onRowExpanding={onRowExpanding}
            onInitNewRow={onInitNewRow}
            onSelectionChanged={onSelectionChanged}
            remoteOperations={remoteOperations}
            repaintChangesOnly={true}
            rowAlternationEnabled={rowAlternationEnabled}
            showBorders={showBorders}
            showColumnHeaders={showColumnHeaders}
            showRowLines={showRowLines}
            width={width}
            wordWrapEnabled={wordWrapEnabled}
            noDataText={noDataText}
            height={height}
        >
            <KeyboardNavigation
                editOnKeyPress={true}
                enterKeyAction='startEdit'
                enterKeyDirection='column'
            />
            <Selection mode={selectionMode} />
            {children}
            <Paging
                defaultPageSize={defaultPageSize}
                enabled={defaultPageSize > 0}
            />
            {defaultPageSize > 0 && (
                <Pager
                    showPageSizeSelector={allowedPageSizes.length > 1}
                    allowedPageSizes={allowedPageSizes}
                    showNavigationButtons={true}
                    showInfo={true}
                    visible={pagerVisible}
                />
            )}
        </DataGrid>
    );
};
